/**
 * One rule for what order a table's columns go in.
 *
 * Left to itself every screen ordered its columns by whatever the payload
 * happened to serialise, so a reader scanning a table met a raw uuid or a
 * timestamp before they met the thing the row is about. The order below is
 * fixed and shared instead, so a new table inherits it rather than deciding
 * it again:
 *
 *   1. identity   - the human-meaningful name of the thing
 *   2. state      - status, outcome, severity: does this row matter?
 *   3. measure    - counts, rates, durations, proportions
 *   4. time       - when it happened, when it expires
 *   5. incidental - internal ids, correlation ids, revisions
 *
 * A table whose organising principle IS time - an activity log, a delivery
 * feed - declares its own leading columns and keeps them; declared order
 * always wins, and this rule orders everything the screen did not declare.
 */

export const COLUMN_BANDS = ['identity', 'state', 'measure', 'time', 'incidental'] as const;

export type ColumnBand = (typeof COLUMN_BANDS)[number];

/** Exact keys that name the subject of a row rather than describe it. */
const IDENTITY_KEYS = new Set([
  'name',
  'title',
  'label',
  'email',
  'subject',
  'kind',
  'family',
  'workflow',
  'route',
  'path',
  'endpoint',
  'application',
  'app',
  'event',
  'event_type',
  'setting',
  'flag',
  'feature',
  'model',
  'member',
  'stylist',
  'operator',
  'task',
  'taskname',
  'queue',
  'key',
  'slug',
  'code',
]);

const IDENTITY_SUFFIXES = ['_name', '_email', '_title', '_label', '_type'];

const STATE_KEYS = new Set([
  'status',
  'state',
  'outcome',
  'severity',
  'result',
  'verdict',
  'phase',
  'stage',
  'health',
  'enabled',
  'disabled',
  'active',
  'suppressed',
  'availability',
  'level',
  'role',
  'priority',
  'reason',
  'reason_code',
  'errorcode',
  'error_code',
  'last_error_code',
]);

const STATE_SUFFIXES = ['_status', '_state', '_severity', '_outcome', '_health'];

const TIME_SUFFIXES = ['_at', '_on', '_date', '_time', '_after', '_until', '_since'];

const TIME_KEYS = new Set(['timestamp', 'date', 'time', 'occurred', 'expires', 'when']);

const INCIDENTAL_KEYS = new Set([
  'id',
  'uuid',
  'guid',
  'revision',
  'version',
  'etag',
  'hash',
  'digest',
  'checksum',
  'cursor',
]);

const INCIDENTAL_SUFFIXES = ['_id', '_uuid', '_reference', '_ref', '_key', '_hash', '_version'];

function normalise(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

function endsWithAny(key: string, suffixes: readonly string[]): boolean {
  return suffixes.some((suffix) => key.endsWith(suffix));
}

/**
 * Which band a column falls in. Incidental is tested before identity so that
 * `member_id` reads as plumbing even though `member` would read as identity,
 * and state before time so `activation_delivery_status` is state rather than
 * a `_status`-shaped nothing.
 */
export function columnBand(key: string): ColumnBand {
  const normalised = normalise(key);
  if (INCIDENTAL_KEYS.has(normalised) || endsWithAny(normalised, INCIDENTAL_SUFFIXES))
    return 'incidental';
  if (STATE_KEYS.has(normalised) || endsWithAny(normalised, STATE_SUFFIXES)) return 'state';
  if (IDENTITY_KEYS.has(normalised) || endsWithAny(normalised, IDENTITY_SUFFIXES))
    return 'identity';
  if (TIME_KEYS.has(normalised) || endsWithAny(normalised, TIME_SUFFIXES)) return 'time';
  return 'measure';
}

/**
 * Order a set of column keys by what a reader needs first.
 *
 * `declared` columns keep their given order and lead, because a screen that
 * has thought about its own table knows more than this rule does. Everything
 * else is banded. Within a band the payload's own order is kept, so two
 * equally informative measures do not shuffle between renders.
 *
 * The one promotion: a table with no human-meaningful identity column at all
 * leads with its first id rather than burying it, because an opaque id is
 * still better than opening with a duration.
 */
export function orderColumnsByValue(
  keys: readonly string[],
  declared: readonly string[] = [],
): string[] {
  const leading = declared.filter((key) => keys.includes(key));
  // A screen that names its columns has made a judgement about what belongs on
  // screen; honour it. Everything in the record is still on the row detail.
  if (leading.length) return leading;
  const rest = keys.filter((key) => !leading.includes(key));
  const banded = new Map<ColumnBand, string[]>(COLUMN_BANDS.map((band) => [band, []]));
  for (const key of rest) banded.get(columnBand(key))?.push(key);

  const identity = banded.get('identity') ?? [];
  const incidental = banded.get('incidental') ?? [];
  const leadsWithIdentity = leading.some((key) => columnBand(key) === 'identity');
  if (!identity.length && !leadsWithIdentity && incidental.length) {
    // No name exists anywhere in the payload: this is a backend-field gap, and
    // the least-bad reading is the id at the front rather than after the dates.
    identity.push(incidental.shift() as string);
  }

  return [
    ...leading,
    ...identity,
    ...(banded.get('state') ?? []),
    ...(banded.get('measure') ?? []),
    ...(banded.get('time') ?? []),
    ...incidental,
  ];
}
